"use client";

import { useStytchB2BClient } from "@stytch/nextjs/b2b";
import type { DiscoveredOrganization } from "@stytch/vanilla-js";
import { useCallback } from "react";
import { reportAuthError } from "../../lib/analytics";

const THIRTY_DAYS = 43200; // 30 days in minutes

/**
 * Utility function to read a cookie value by name
 */
function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }
  const nameEQ = `${name}=`;
  const parts = document.cookie.split(";");
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed.startsWith(nameEQ)) {
      return decodeURIComponent(trimmed.substring(nameEQ.length));
    }
  }
  return undefined;
}

/**
 * Utility function to get current organization from user-preferences cookie
 */
function getCurrentOrgFromCookie(): string | null {
  try {
    const userPreferences = readCookie("user-preferences");
    if (!userPreferences) {
      return null;
    }
    const preferences = JSON.parse(userPreferences);
    return preferences["current-org"] || null;
  } catch {
    return null;
  }
}

/**
 * Hook that implements the decision tree for organization selection after authentication
 *
 * Decision tree:
 * 1. If only 1 org, log in immediately
 * 2. If there's a most recent org stored in user-preferences cookie, log in to that one
 * 3. If 2 orgs, sign into the one that is not named "Personal"
 * 4. Otherwise, show the discovery page (redirect to organization-select)
 */
export const useOrganizationDecisionTree = (
  onAuthComplete: () => Promise<void>,
) => {
  const stytchB2BClient = useStytchB2BClient();

  const getMostRecentOrg = useCallback((): string | null => {
    return getCurrentOrgFromCookie();
  }, []);

  const loginToOrganization = useCallback(
    async (orgId: string) => {
      try {
        // Exchange the intermediate session token for a full session with the selected org
        await stytchB2BClient.discovery.intermediateSessions.exchange({
          organization_id: orgId,
          session_duration_minutes: THIRTY_DAYS,
        });

        // Complete the authentication flow
        await onAuthComplete();
      } catch (error) {
        reportAuthError(`Failed to login to organization: ${error}`);
        // Fallback to discovery page
        // Aka do nothing
      }
    },
    [stytchB2BClient, onAuthComplete],
  );

  const handleOrganizationSelection = useCallback(
    async (discoveredOrganizations: DiscoveredOrganization[]) => {
      const orgs = discoveredOrganizations;

      // Decision tree implementation
      if (orgs.length === 1 && orgs?.[0]?.organization.organization_id) {
        // Rule 1: If only 1 org, log in immediately
        // console.log("Rule 1: If only 1 org, log in immediately");
        await loginToOrganization(orgs?.[0]?.organization.organization_id);
        return;
      }

      // Rule 2: If there's a most recent org stored, log in to that one
      const mostRecentOrgId = getMostRecentOrg();

      if (mostRecentOrgId) {
        // console.log(
        //   "Rule 2: If there's a most recent org stored, log in to that one searching...",
        // );
        const matchingOrg = orgs.find(
          (org) => org.organization.organization_id === mostRecentOrgId,
        );
        if (matchingOrg) {
          //   console.log(
          //     "Rule 2: If there's a most recent org stored, log in to that one, found it!",
          //   );
          await loginToOrganization(mostRecentOrgId);
          return;
        }
      }

      // Rule 3: If 2 orgs, sign into the one that is not named "Personal"
      if (orgs.length === 2) {
        // console.log(
        //   "Rule 3: If 2 orgs, sign into the one that is not named 'Personal'",
        // );
        const nonPersonalOrg = orgs.find(
          (org) =>
            org.organization.organization_name.toLowerCase() !== "personal",
        );
        if (nonPersonalOrg) {
          await loginToOrganization(
            nonPersonalOrg.organization.organization_id,
          );
          return;
        }
        // If both are named "Personal" or neither matches, fall through to discovery page
      }

      // Rule 4: Otherwise, show the discovery page
      //////////////////////////////////////////////////////////////
      // Aka do nothing
      //////////////////////////////////////////////////////////////
    },
    [loginToOrganization, getMostRecentOrg],
  );

  return { handleOrganizationSelection } as const;
};
